import React from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Calendar, Linkedin, Mail, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import LoadingIndicator from "@/components/LoadingIndicator/LoadingIndicator";
import YouTubeEmbed from "@/modules/hub/features/EmployeeDirectory/components/YouTubeEmbed/YouTubeEmbed";
import { useProfileDetailsPage } from "@/modules/hub/features/EmployeeDirectory/hooks/useProfileDetailsPage";
import { Employee } from "@/modules/hub/features/EmployeeDirectory/api/fetchEmployeeDirectoryProfiles/types";
import { formatPhoneNumber } from "@/utils/formatPhoneNumber";

const memberDisplayName = (member: Employee): string => {
  const first = member.employee.firstName ?? "";
  const last = member.employee.lastName ?? "";
  const full = `${first} ${last}`.trim();
  return full.length > 0 ? full : member.email || member.uuid;
};

const memberFirstName = (member: Employee): string => {
  return member.employee.firstName?.trim() || memberDisplayName(member);
};

const ProfileDetailsPage: React.FC = () => {
  const { uuid } = useParams<{ uuid: string }>();
  const { loading, member } = useProfileDetailsPage(uuid);

  if (loading) {
    return (
      <div className="py-20">
        <LoadingIndicator />
      </div>
    );
  }

  if (!member) {
    return (
      <div className="bg-white dark:bg-slate-900 min-h-screen">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-12 text-center">
          <p className="text-lg text-gray-700 dark:text-gray-200">
            Team member not found.
          </p>
          <Button asChild className="mt-6" variant="outline">
            <Link to="/employee-directory">
              <ArrowLeft className="mr-2 h-4 w-4" />
              All Team Members
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  const name = memberDisplayName(member);
  const firstName = memberFirstName(member);

  return (
    <div className="bg-white dark:bg-slate-900 min-h-screen">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 pt-12 pb-16">
        <div className="text-center">
          <p className="text-xs font-semibold tracking-widest text-primary uppercase">
            Meet Our Team
          </p>
          <h1 className="mt-2 text-4xl md:text-5xl font-bold tracking-tight text-gray-900 dark:text-white">
            {name}
          </h1>
          {member.role && (
            <p className="mt-2 text-base font-semibold text-gray-700 dark:text-gray-300">
              {member.role}
            </p>
          )}
        </div>

        <div className="mt-10 grid grid-cols-1 gap-8 lg:grid-cols-3">
          <div className="space-y-8 lg:col-span-2">
            {member.isYoutubeUrlEnabled && (
              <YouTubeEmbed
                fallbackImageUrl={member.photo.publicUrl}
                memberName={firstName}
                url={member.youtubeUrl}
              />
            )}

            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                About {firstName}
              </h2>
              {member.bio ? (
                <p className="mt-3 whitespace-pre-line text-base text-gray-700 dark:text-gray-300">
                  {member.bio}
                </p>
              ) : (
                <p className="mt-3 text-base italic text-gray-500 dark:text-gray-400">
                  No bio yet.
                </p>
              )}
            </div>

            {member.specialties.length > 0 && (
              <div>
                <p className="text-xs font-semibold tracking-widest text-gray-500 dark:text-gray-400 uppercase">
                  Trade Specialties
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {member.specialties.map((s) => (
                    <span
                      className="rounded-full border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-4 py-1 text-sm text-gray-700 dark:text-gray-200"
                      key={s.uuid}
                    >
                      {s.name}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          <aside className="space-y-6">
            <div className="overflow-hidden rounded-2xl border border-gray-200 dark:border-slate-700 bg-gray-100 dark:bg-slate-700 aspect-square">
              {member.photo.publicUrl ? (
                <img
                  alt={name}
                  className="h-full w-full object-cover"
                  src={member.photo.publicUrl}
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center text-5xl font-light text-gray-400 dark:text-slate-500">
                  {(member.employee.firstName?.charAt(0) ?? "?").toUpperCase()}
                </div>
              )}
            </div>

            <div className="rounded-[14px] border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800 p-6">
              <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-primary">
                Contact {firstName}
              </p>

              <div className="mt-5 flex flex-col gap-3">
                {member.primaryPhoneNumber && (
                  <a
                    className="flex items-center gap-3 rounded-[10px] bg-white dark:bg-slate-900 px-4 py-3 text-gray-700 dark:text-gray-200 transition-colors hover:bg-primary/10 hover:text-primary"
                    href={`tel:${member.primaryPhoneNumber}`}
                  >
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                      <Phone className="h-[15px] w-[15px] text-primary" />
                    </span>
                    <span className="flex flex-col gap-px">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">
                        Phone
                      </span>
                      <span className="text-[13px] font-semibold">
                        {formatPhoneNumber(member.primaryPhoneNumber)}
                      </span>
                    </span>
                  </a>
                )}

                {member.email && (
                  <div className="flex items-center gap-2">
                    <a
                      className="flex flex-1 items-center gap-3 overflow-hidden rounded-[10px] bg-white dark:bg-slate-900 px-4 py-3 text-gray-700 dark:text-gray-200 transition-colors hover:bg-primary/10 hover:text-primary"
                      href={`mailto:${member.email}`}
                    >
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                        <Mail className="h-[15px] w-[15px] text-primary" />
                      </span>
                      <span className="flex flex-col gap-px overflow-hidden">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">
                          Email
                        </span>
                        <span className="truncate text-[13px] font-semibold">
                          {member.email}
                        </span>
                      </span>
                    </a>
                    {member.linkedInUrl && (
                      <a
                        aria-label={`${name} on LinkedIn`}
                        className="flex h-[52px] w-[52px] shrink-0 items-center justify-center rounded-[10px] bg-[#0a66c2] text-white transition-colors hover:bg-[#004182]"
                        href={member.linkedInUrl}
                        rel="noopener noreferrer"
                        target="_blank"
                        title={`Connect with ${firstName} on LinkedIn`}
                      >
                        <Linkedin className="h-[18px] w-[18px]" />
                      </a>
                    )}
                  </div>
                )}
              </div>

              <div className="mt-5 flex flex-col gap-2.5">
                {member.bookCallUrl ? (
                  <a
                    className="flex items-center justify-center gap-2 rounded-lg bg-primary px-5 py-3 text-[13.5px] font-bold tracking-wide text-white transition-colors hover:bg-primary/90"
                    href={member.bookCallUrl}
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    <Calendar className="h-3.5 w-3.5" />
                    Book a Meeting
                  </a>
                ) : (
                  <button
                    className="flex cursor-not-allowed items-center justify-center gap-2 rounded-lg bg-primary/50 px-5 py-3 text-[13.5px] font-bold tracking-wide text-white"
                    disabled
                    type="button"
                  >
                    <Calendar className="h-3.5 w-3.5" />
                    Book a Meeting
                  </button>
                )}
              </div>
            </div>

            <Link
              className="flex items-center justify-center gap-2 rounded-lg border-[1.5px] border-gray-200 bg-white px-5 py-3 text-[13.5px] font-semibold text-gray-700 transition-colors hover:border-primary hover:text-primary dark:border-slate-700 dark:bg-slate-900 dark:text-gray-200"
              to="/employee-directory"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              All Team Members
            </Link>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default ProfileDetailsPage;
